import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchContextualHelpItems } from 'api/contextualHelpService';
import { useChat } from 'components/chat/ChatContext';
import { useAuth } from 'contexts/AuthContext';

const ContextualHelpContext = createContext(null);

export function ContextualHelpProvider({ children }) {
  const { i18n, t } = useTranslation();
  const { openContextualHelp } = useChat();
  const { user } = useAuth();
  const [itemsByKey, setItemsByKey] = useState({});

  useEffect(() => {
    if (!user) return;
    void fetchContextualHelpItems()
      .then((items) => {
        const map = {};
        for (const item of items) {
          map[item.contextKey] = item;
        }
        setItemsByKey(map);
      })
      .catch(() => setItemsByKey({}));
  }, [user]);

  const hasContext = useCallback((contextKey) => Boolean(itemsByKey[contextKey]), [itemsByKey]);

  const forContext = useCallback(
    async (contextKey) => {
      const item = itemsByKey[contextKey];
      if (!item) return;
      const prompt = item.prompt;
      await openContextualHelp(prompt, i18n.language, contextKey);
    },
    [itemsByKey, i18n.language, openContextualHelp]
  );

  const value = useMemo(
    () => ({
      hasContext,
      ask: {
        tooltipLabel: t('contextualHelp.askAi'),
        forContext
      }
    }),
    [hasContext, forContext, t]
  );

  return <ContextualHelpContext.Provider value={value}>{children}</ContextualHelpContext.Provider>;
}

export function useContextualHelp() {
  const ctx = useContext(ContextualHelpContext);
  if (!ctx) throw new Error('useContextualHelp must be used within ContextualHelpProvider');
  return ctx;
}
